import { Ether, Percent, Token } from '@uniswap/sdk-core'
import { TickMath } from '@uniswap/v3-sdk'
import { Pool, Position, V4PositionManager } from '@uniswap/v4-sdk'
import type { DecreaseLPPositionResponse } from 'uniswap/src/data/tradingApi/__generated__'
import { DecreaseLPPositionRequest } from 'uniswap/src/data/tradingApi/__generated__'
import { getUniswapWagmiPublicClient } from 'uniswap/src/features/providers/wagmiBridge'
import {
  DELI_HOOK_CP_ABI,
  DELI_HOOK_CP_ADDRESS,
  ZERO_ADDRESS,
  ZERO_BYTES32,
  alignTicks,
  estimateGasFee,
  fetchDecimals,
  getV4PositionManagerAddress,
  readConstantProductReserves,
  readConstantProductUserBalance,
  readPoolState,
} from 'uniswap/src/features/transactions/liquidity/steps/shared'
import { OnChainTransactionFields, TransactionStepType } from 'uniswap/src/features/transactions/steps/types'
import { ValidatedTransactionRequest } from 'uniswap/src/features/transactions/swap/utils/trade'
import { logger } from 'utilities/src/logger/logger'
import { encodeFunctionData } from 'viem'

export interface DecreasePositionTransactionStep extends OnChainTransactionFields {
  // Doesn't require permit
  type: TransactionStepType.DecreasePositionTransaction
}

export function createDecreasePositionStep(txRequest: ValidatedTransactionRequest): DecreasePositionTransactionStep {
  return {
    type: TransactionStepType.DecreasePositionTransaction,
    txRequest,
  }
}

type ErrorWithDebugFields = {
  name?: string
  message?: string
  shortMessage?: string
  details?: string
  code?: string | number
  data?: unknown
  metaMessages?: unknown
  cause?: unknown
}

function getErrorDebugDetails(error: unknown, depth = 0): unknown {
  if (!error || typeof error !== 'object') {
    return error
  }

  const parsedError = error as ErrorWithDebugFields
  const details: Record<string, unknown> = {
    name: parsedError.name,
    message: parsedError.message,
    shortMessage: parsedError.shortMessage,
    details: parsedError.details,
    code: parsedError.code,
    data: parsedError.data,
    metaMessages: parsedError.metaMessages,
  }

  if (error instanceof Error) {
    details.stack = error.stack
  }

  if (parsedError.cause && depth < 2) {
    details.cause = getErrorDebugDetails(parsedError.cause, depth + 1)
  }

  return details
}

export async function buildV4DecreasePositionTx(args: DecreaseLPPositionRequest): Promise<ValidatedTransactionRequest> {
  const resp = await buildV4DecreasePositionResponse(args)
  const tx = resp.decrease
  if (!tx) throw new Error('decrease builder failed to produce tx')
  const valueStr = tx.value ?? '0'
  return {
    chainId: Number(args.chainId),
    from: tx.from,
    to: tx.to,
    data: tx.data as `0x${string}`,
    value: valueStr !== '0' ? BigInt(valueStr) : undefined,
  }
}

export async function buildV4DecreasePositionResponse(
  args: DecreaseLPPositionRequest,
): Promise<DecreaseLPPositionResponse> {
  const chainId = Number(args.chainId)
  const from = args.walletAddress as `0x${string}`
  const tokenId = String(args.tokenId ?? 0)
  const client = getUniswapWagmiPublicClient(chainId)
  if (!client) throw new Error('Missing viem public client')

  const percentage = new Percent(Math.floor(Number(args.liquidityPercentageToDecrease ?? 0) * 100), 10_000)

  // Build pool and position for removal
  const poolReq = args.position?.pool
  if (!poolReq) throw new Error('Missing pool in args')
  const fee = Number(poolReq.fee)
  const tickSpacing = Number(poolReq.tickSpacing)
  const hooks = (poolReq as any).hooks as `0x${string}` | undefined
  const isConstantProductHook = hooks?.toLowerCase() === DELI_HOOK_CP_ADDRESS.toLowerCase()
  const token0Addr = poolReq.token0 as `0x${string}`
  const token1Addr = poolReq.token1 as `0x${string}`
  const currency0 =
    token0Addr === ZERO_ADDRESS
      ? Ether.onChain(chainId)
      : new Token(chainId, token0Addr, await fetchDecimals(client, token0Addr))
  const currency1 =
    token1Addr === ZERO_ADDRESS
      ? Ether.onChain(chainId)
      : new Token(chainId, token1Addr, await fetchDecimals(client, token1Addr))
  let sqrtRatioX96Str = args.sqrtRatioX96
  let poolLiquidityStr = args.poolLiquidity
  let currentTickVal2 = args.currentTick
  if (!sqrtRatioX96Str || !poolLiquidityStr || currentTickVal2 === undefined) {
    const state = await readPoolState(
      client as any,
      chainId,
      currency0 as any,
      currency1 as any,
      fee,
      tickSpacing,
      hooks,
    )
    sqrtRatioX96Str = sqrtRatioX96Str ?? state.sqrtRatioX96
    poolLiquidityStr = poolLiquidityStr ?? state.poolLiquidity
    currentTickVal2 = currentTickVal2 ?? state.currentTick
  }

  // CP hook removal path
  if (isConstantProductHook) {
    // Read reserves and user balance
    const { reserve0, reserve1, totalSupply } = await readConstantProductReserves(
      client as any,
      chainId,
      currency0 as any,
      currency1 as any,
      fee,
      tickSpacing,
    )
    const userShares = await readConstantProductUserBalance(
      client as any,
      chainId,
      currency0 as any,
      currency1 as any,
      fee,
      tickSpacing,
      from,
    )

    if (totalSupply === BigInt(0)) {
      throw new Error('Pool has zero total supply')
    }
    if (userShares === BigInt(0)) {
      throw new Error('No liquidity to remove')
    }

    const pctNum = BigInt(percentage.numerator.toString())
    const pctDen = BigInt(percentage.denominator.toString())
    const liquidityToRemove = (userShares * pctNum) / pctDen
    if (liquidityToRemove === BigInt(0)) {
      throw new Error('Selected percentage too small')
    }

    const amt0Expected = (reserve0 * liquidityToRemove) / totalSupply
    const amt1Expected = (reserve1 * liquidityToRemove) / totalSupply

    const slipNum = BigInt(Math.floor(Number(args.slippageTolerance ?? 50) * 100))
    const slipDen = BigInt(10_000)
    const amount0Min =
      amt0Expected === BigInt(0) || slipDen === BigInt(0)
        ? amt0Expected
        : amt0Expected - (amt0Expected * slipNum) / slipDen
    const amount1Min =
      amt1Expected === BigInt(0) || slipDen === BigInt(0)
        ? amt1Expected
        : amt1Expected - (amt1Expected * slipNum) / slipDen

    const deadlineSec = BigInt(String(args.deadline ?? Math.floor(Date.now() / 1000) + 20 * 60))

    const removeCalldata = encodeFunctionData({
      abi: DELI_HOOK_CP_ABI,
      functionName: 'removeLiquidity',
      args: [
        {
          currency0: token0Addr,
          currency1: token1Addr,
          fee,
          tickSpacing,
          hooks: DELI_HOOK_CP_ADDRESS,
        },
        {
          liquidity: liquidityToRemove,
          amount0Min,
          amount1Min,
          deadline: deadlineSec,
          tickLower: 0,
          tickUpper: 0,
          userInputSalt: ZERO_BYTES32,
        },
      ],
    }) as `0x${string}`

    let gasFee: string | undefined
    if (args.simulateTransaction) {
      try {
        gasFee = await estimateGasFee(client as any, from, DELI_HOOK_CP_ADDRESS, removeCalldata)
      } catch (error) {
        logger.error(error, {
          tags: {
            file: 'decreasePosition',
            function: 'buildV4DecreasePositionResponse',
          },
          extra: {
            simulationPath: 'constantProduct.removeLiquidity',
            requestArgs: args,
            txRequest: {
              chainId,
              from,
              to: DELI_HOOK_CP_ADDRESS,
              value: '0',
              data: removeCalldata,
            },
            errorDetails: getErrorDebugDetails(error),
          },
        })
        throw error
      }
    }

    return {
      requestId: undefined,
      decrease: {
        to: DELI_HOOK_CP_ADDRESS,
        from,
        data: removeCalldata,
        value: '0',
        chainId: chainId as any,
      },
      gasFee,
      poolLiquidity: poolLiquidityStr,
      currentTick: currentTickVal2,
      sqrtRatioX96: sqrtRatioX96Str,
    }
  }
  const pool = new Pool(
    currency0,
    currency1,
    fee,
    tickSpacing,
    hooks ?? ZERO_ADDRESS,
    sqrtRatioX96Str,
    poolLiquidityStr,
    currentTickVal2,
  )
  const rawTickLower = args.position?.tickLower ?? TickMath.MIN_TICK
  const rawTickUpper = args.position?.tickUpper ?? TickMath.MAX_TICK
  const { tickLower, tickUpper } = alignTicks(Number(rawTickLower), Number(rawTickUpper), tickSpacing)
  const pos = new Position({ pool, tickLower, tickUpper, liquidity: (args.positionLiquidity ?? '0') as any })

  const options: any = {
    slippageTolerance: new Percent(Number(args.slippageTolerance ?? 50), 10_000),
    deadline: String(args.deadline ?? Math.floor(Date.now() / 1000) + 20 * 60),
    hookData: '0x',
    tokenId,
    liquidityPercentage: percentage,
  }
  const { calldata, value } = V4PositionManager.removeCallParameters(pos, options)

  // Simulate gas if requested
  let gasFee: string | undefined
  if (args.simulateTransaction) {
    const txValue = value ? BigInt(value as any) : undefined

    try {
      gasFee = await estimateGasFee(
        client,
        from,
        getV4PositionManagerAddress(chainId),
        calldata as `0x${string}`,
        txValue,
      )
    } catch (error) {
      logger.error(error, {
        tags: {
          file: 'decreasePosition',
          function: 'buildV4DecreasePositionResponse',
        },
        extra: {
          simulationPath: 'v4PositionManager.removeCallParameters',
          requestArgs: args,
          txRequest: {
            chainId,
            from,
            to: getV4PositionManagerAddress(chainId),
            value: txValue?.toString() ?? '0',
            data: calldata,
          },
          errorDetails: getErrorDebugDetails(error),
        },
      })
      throw error
    }
  }

  // Echo pool state if provided (optional, best-effort)
  const currentTick = args.currentTick
  const poolLiquidity = args.poolLiquidity
  const sqrtRatioX96 = args.sqrtRatioX96

  return {
    requestId: undefined,
    decrease: {
      to: getV4PositionManagerAddress(chainId),
      from,
      data: calldata as string,
      value: value ? String(value) : '0',
      chainId: chainId as any,
    },
    gasFee,
    poolLiquidity,
    currentTick,
    sqrtRatioX96,
  }
}
